/**
 * What the ORDER BOOK shows, and how much of it at once.
 *
 * Pure and React-free: "is this row from today" and "how many rows are
 * visible" are both decisions worth asserting rather than eyeballing, and
 * the first one is a timezone question, which is where these go wrong.
 */

/** Rows revealed at a time — the first page, and each press of Load more. */
export const ORDER_PAGE = 50

/**
 * The IST calendar day a timestamp falls on, as `YYYY-MM-DD`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY IST AND NOT THE BROWSER'S ZONE
 *
 * A trading day is an EXCHANGE day. NSE opens 09:15 IST and closes 15:30
 * IST, and "today's orders" means that session — not the span of the
 * viewer's local midnight, which for a trader travelling, or for a desk
 * machine left on UTC, would cut the session in half or merge two of them.
 *
 * `en-CA` is used because it formats as `YYYY-MM-DD`, which compares
 * correctly as a string. Nothing here parses the result; it is only ever
 * compared to another value from this same function.
 * ───────────────────────────────────────────────────────────────────────────
 */
export function istDayKey(ms: number): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(ms))
}

/**
 * Only the rows from the current IST trading day.
 *
 * The order book is a SESSION document. Yesterday's fills are the journal's
 * business, and leaving them here is what turned a tab a trader opens to
 * see what just happened into a scroll through a thousand settled rows.
 */
export function ordersToday<T extends { at: number }>(
  rows: readonly T[],
  now: number,
): readonly T[] {
  const today = istDayKey(now)
  // Identity when every row already qualifies, so a memoising caller does
  // not re-render on a new array holding the same rows.
  if (rows.every((r) => istDayKey(r.at) === today)) return rows
  return rows.filter((r) => istDayKey(r.at) === today)
}
